import { AUTH_CODE_LENGTH } from '@repo/domain';
import { tr } from '@repo/locale';
import { type TestId } from '@repo/test-id';
import { toast } from '@repo/toast';
import { ArrowLeftIcon, XIcon } from '@repo/icons';
import { Button, CodeInputGroup } from '@repo/ui';

import CodeResendButton from '@features/auth/components/CodeResendButton';
import { AuthModalException } from '@features/auth/hooks/useAuthModal';

interface Props {
  testId: TestId;
  email: string;
  confirmCode: (code: string) => Promise<unknown>;
  resendCode: () => Promise<unknown>;
  changeEmail: () => void;
  resendSecondsLeft: number;
  requesting: boolean;
  validating: boolean;
  finalizing: boolean;
  initialEmail?: string;
  onClose?: () => void;
}

export default function CodeSection({
  testId,
  email,
  confirmCode,
  resendCode,
  changeEmail,
  resendSecondsLeft,
  requesting,
  validating,
  finalizing,
  initialEmail,
  onClose,
}: Props) {
  const handleConfirmCode = async (code: string) => {
    if (validating) return;
    try {
      await confirmCode(code);
    } catch (error) {
      if (error instanceof AuthModalException) {
        toast.danger(tr('auth:login_drawer.auth_code.invalid_code'), testId.append('invalidCodeToast'));
        return;
      }
      toast.danger(tr('auth:login_drawer.confirm_code_error'), testId.append('confirmCodeErrorToast'));
    }
  };

  const handleResendCode = async () => {
    if (requesting) return;
    try {
      await resendCode();
      toast.success(tr('auth:login_drawer.auth_code.resend_info'), testId.append('resendInfoToast'));
    } catch (error) {
      if (error instanceof AuthModalException) {
        toast.danger(tr('auth:login_drawer.request_code_error'), testId.append('resendErrorToast'));
      }
    }
  };

  const handlePasteError = () => {
    toast.danger(tr('reservations:blik.clipboard_access_denied'), testId.append('clipboardError'));
  };

  return (
    <>
      <div className="flex justify-between">
        <h2 className="text-heading text-xl font-semibold">{tr('auth:login_drawer.auth_code.title')}</h2>
        {onClose && (
          <button aria-label={tr('actions.close')} className="cursor-pointer" type="button" onClick={onClose}>
            <XIcon size={20} />
          </button>
        )}
      </div>
      <p className="text-body text-sm">
        {tr('auth:login_drawer.auth_code.description')} <span className="font-semibold">{email}</span>
      </p>

      <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
        <Button
          onClick={initialEmail ? onClose : changeEmail}
          leftIcon={ArrowLeftIcon}
          testId={testId.append('changeEmailButton')}
        >
          {tr('auth:login_drawer.auth_code.change_email')}
        </Button>
        <CodeResendButton
          testId={testId.append('resendCodeButton')}
          secondsLeft={resendSecondsLeft}
          pending={requesting}
          onClick={handleResendCode}
          email={email}
        />
      </div>

      <div className="mx-auto max-w-70">
        <div className="mb-3 text-center">{tr('auth:login_drawer.auth_code.code_label')}</div>
        <CodeInputGroup
          variant="connected"
          length={AUTH_CODE_LENGTH}
          onComplete={handleConfirmCode}
          testId={testId.append('codeInput')}
          disabled={validating || finalizing}
          pasteButtonLabel={tr('auth:login_drawer.auth_code.paste_code')}
          onPasteError={handlePasteError}
          resetOnComplete
          required
          autoFocus
        />
      </div>
    </>
  );
}
